/*
 * Direct RudderStack event poster for first-party custom events.
 *
 * Why this exists:
 *   The Shopify-installed RudderStack pixel only forwards a fixed set of
 *   standard Shopify Customer Event names. Our custom events (PDP engagement,
 *   cart_viewed, customize_form_submitted, ...) are never forwarded, so we
 *   send them ourselves.
 *
 * Transport — RudderStack HTTP API source (best practice, verified against
 * RudderStack docs 2026-06-29):
 *   POST {dataplane}/v1/track   with HTTP Basic auth (writeKey as username,
 *   empty password). The HTTP source preserves the event name, anonymousId,
 *   and properties EXACTLY as sent — so events land as e.g.
 *   `customize_form_submitted` (not the generic `webhook_source_event` a
 *   Webhook source would wrap them in) and keep our anonymousId so they join
 *   to the Shopify-source events.
 *
 *   HISTORY: this used to POST to /v1/webhook against the Shopify source key
 *   (silently dropped, HTTP 200), then a Webhook source (which wraps every
 *   payload as `webhook_source_event` and discards anonymousId). Both wrong.
 *   The HTTP source below is the correct tool.
 *
 * Credentials (PUBLIC write key — safe to ship in theme JS; permits WRITE
 * only, not READ):
 *   source type:  HTTP API
 *   writeKey:     3Fo1iekPYnJpSs6qtco8pPUQ88m
 *   source_id:    3Fo1ilUckyQ3RQQknOWwC1isqzm
 *   data plane:   autonomousfwaw.dataplane.rudderstack.com
 *
 * Lands in: rudder_cdp.{event_name} (one table per distinct event name),
 * tagged with this source_id; join to Shopify-source events via anonymous_id.
 *
 * 2026-08-06 additions:
 *   - window.rudderIdentify(traits): POST /v1/identify so an email captured
 *     anywhere on the site links to this visitor's anonymous_id. Before this,
 *     popup signups landed in Shopify only and never joined to browse/cart
 *     history (identifies table was empty for this store).
 *   - whatsapp_clicked: the WhatsApp button is a third-party app embed
 *     (webse-wp-button) we cannot edit; a capture-phase listener on document
 *     catches clicks on any wa.me / whatsapp link. Also fires Meta 'Contact'
 *     so ads can optimise toward it.
 *   - email_captured + identify on any Shopify Forms embed submit (newsletter
 *     popup, etc.). Same open-shadow-DOM technique as atelier-form-track.js.
 */
(function () {
  if (typeof window === 'undefined') return;
  if (window.rudderTrack) return; // idempotent

  var WRITE_KEY = '3Fo1iekPYnJpSs6qtco8pPUQ88m';
  var DATAPLANE = 'https://autonomousfwaw.dataplane.rudderstack.com';
  var SOURCE_ID = '3Fo1ilUckyQ3RQQknOWwC1isqzm';

  // Pull the Shopify anonymous-visitor id from the standard `_shopify_y`
  // cookie. This is the same id Shopify Customer Events stamps as `clientId`,
  // so events sent here are joinable to events from the standard pixel path
  // on the same visitor.
  function getAnonymousId() {
    try {
      var match = document.cookie.match(/(?:^|;\s*)_shopify_y=([^;]+)/);
      if (match) return decodeURIComponent(match[1]);
    } catch (e) {}
    return null;
  }

  function post(path, body) {
    // keepalive lets the request finish even if the page is unloading
    // (e.g. the user navigates away right after the event fires).
    fetch(DATAPLANE + path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Basic auth: username = writeKey, password = empty.
        'Authorization': 'Basic ' + btoa(WRITE_KEY + ':')
      },
      body: JSON.stringify(body),
      keepalive: true,
      mode: 'cors',
      credentials: 'omit'
    }).catch(function () { /* fire-and-forget */ });
  }

  function baseContext() {
    return {
      source_id: SOURCE_ID,
      library: { name: 'rashmi-theme-http' },
      page: {
        path: window.location.pathname,
        url: window.location.href,
        referrer: document.referrer || '',
        title: document.title
      },
      userAgent: navigator.userAgent
    };
  }

  function rudderTrack(eventName, properties) {
    if (!eventName) return;
    try {
      post('/v1/track', {
        type: 'track',
        event: eventName,
        anonymousId: getAnonymousId(),
        properties: properties || {},
        context: baseContext(),
        timestamp: new Date().toISOString()
      });
    } catch (e) { /* never break the page for analytics */ }
  }

  function rudderIdentify(traits) {
    if (!traits || !traits.email) return;
    try {
      post('/v1/identify', {
        type: 'identify',
        anonymousId: getAnonymousId(),
        traits: traits,
        context: baseContext(),
        timestamp: new Date().toISOString()
      });
    } catch (e) { /* never break the page for analytics */ }
  }

  window.rudderTrack = rudderTrack;
  window.rudderIdentify = rudderIdentify;

  /* ---- Meta pixel (manual, contact-intent events ONLY) -------------- */
  // The store's Meta integration runs inside Shopify's sandboxed web pixel,
  // so window.fbq never exists for theme code and contact-intent conversions
  // (WhatsApp 'Contact' below, atelier 'Lead') were silently dropped. This
  // init fires NO standard events — no PageView, no ViewContent — so there is
  // zero duplication with the sandboxed pixel: it exists solely as the pipe
  // for events the sandbox never sends. It reuses the existing _fbp cookie,
  // so Meta joins these to the same browser it already knows.
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
  n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
  document,'script','https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', '4244983132386673');

  /* ---- WhatsApp click tracking ------------------------------------- */
  // The button is an app embed we can't modify, and it may render inside a
  // shadow root or be injected after load — so: one capture-phase listener,
  // composedPath to pierce shadow boundaries, match by href.
  var lastWa = 0;
  document.addEventListener('click', function (evt) {
    try {
      var path = evt.composedPath ? evt.composedPath() : [evt.target];
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        var href = el && el.tagName === 'A' && (el.href || '');
        if (href && (href.indexOf('wa.me') !== -1 || href.indexOf('whatsapp') !== -1)) {
          var now = Date.now();
          if (now - lastWa < 2000) return; // guard double-fire
          lastWa = now;
          // Pre-fill the WhatsApp message: an empty compose box loses most
          // clickers (measured 19 clicks -> 4 messages on 2026-08-06). Name
          // the product when we're on a PDP. Split the title only on
          // spaced separators — product names themselves contain hyphens.
          if (href.indexOf('text=') === -1) {
            var product = location.pathname.indexOf('/products/') === 0
              ? (document.title.split(/\s[|–—]\s/)[0] || '').trim() : '';
            var msg = product
              ? "Hi! I'm interested in the " + product + '. Is it available?'
              : "Hi! I'm interested in your saris.";
            try {
              el.href = href + (href.indexOf('?') === -1 ? '?' : '&') +
                        'text=' + encodeURIComponent(msg);
              href = el.href;
            } catch (e) { /* leave link untouched */ }
          }
          rudderTrack('whatsapp_clicked', { link: href });
          if (typeof window.fbq === 'function') {
            window.fbq('track', 'Contact', {
              content_name: product || 'WhatsApp Button',
              content_category: 'whatsapp'
            });
          }
          return;
        }
      }
    } catch (e) { /* never break the page for analytics */ }
  }, true);

  /* ---- Checkout button feedback ------------------------------------- */
  // Shopify's checkout redirect takes 2-10s on mobile networks with zero
  // visual acknowledgment; users read the frozen button as broken. Delegated
  // listener so it survives the AJAX replacement of #cart-footer.
  (function () {
    var st = document.createElement('style');
    st.textContent =
      '.rk-co-loading{opacity:.75;pointer-events:none}' +
      '.rk-co-loading .rk-co-spin{display:inline-block;width:14px;height:14px;' +
      'margin-right:8px;vertical-align:-2px;border:2px solid currentColor;' +
      'border-right-color:transparent;border-radius:50%;' +
      'animation:rk-co-rot .7s linear infinite}' +
      '@keyframes rk-co-rot{to{transform:rotate(360deg)}}';
    document.head.appendChild(st);
    document.addEventListener('click', function (evt) {
      var path = evt.composedPath ? evt.composedPath() : [evt.target];
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (el && el.tagName === 'BUTTON' && el.name === 'checkout' &&
            !el.classList.contains('rk-co-loading')) {
          // Defer one tick: locking synchronously would drop name=checkout
          // from the form POST and land on /cart instead of checkout.
          setTimeout(function () {
            el.dataset.rkCoLabel = el.textContent;
            el.classList.add('rk-co-loading');
            el.textContent = '';
            var spin = document.createElement('span');
            spin.className = 'rk-co-spin';
            spin.setAttribute('aria-hidden', 'true');
            el.appendChild(spin);
            el.appendChild(document.createTextNode('Taking you to secure checkout…'));
          }, 0);
          return;
        }
      }
    }, true);
    // Back-button (bfcache) restores the page mid-"loading" — reset the button.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      document.querySelectorAll('button.rk-co-loading').forEach(function (b) {
        b.classList.remove('rk-co-loading');
        if (b.dataset.rkCoLabel) b.textContent = b.dataset.rkCoLabel;
      });
    });
  })();

  /* ---- Email capture on any Shopify Forms embed submit -------------- */
  // Same open-shadow-DOM technique as atelier-form-track.js: clicks compose
  // through open shadow roots, so document sees the in-shadow submit button.
  // Any Forms embed (newsletter popup, footer form) with a valid email in it
  // fires an identify — from then on this visitor's email is joined to their
  // whole browse/cart history in the warehouse.
  var lastEmail = 0;
  function findEmail(root) {
    try {
      var inputs = root.querySelectorAll('input[type="email"], input[name*="email" i]');
      for (var i = 0; i < inputs.length; i++) {
        var v = (inputs[i].value || '').trim();
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return v;
      }
      var all = root.querySelectorAll('*');
      for (var j = 0; j < all.length; j++) {
        if (all[j].shadowRoot) {
          var found = findEmail(all[j].shadowRoot);
          if (found) return found;
        }
      }
    } catch (e) { /* ignore */ }
    return '';
  }
  document.addEventListener('click', function (evt) {
    try {
      var path = evt.composedPath ? evt.composedPath() : [];
      var embed = null, submit = false;
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (el && el.tagName === 'SHOPIFY-FORMS-EMBED') embed = el;
        if (el && el.tagName === 'BUTTON' &&
            (el.type === 'submit' || ((el.className || '') + '').indexOf('formSubmitButton') !== -1)) {
          submit = true;
        }
      }
      if (!embed || !submit || !embed.shadowRoot) return;
      var email = findEmail(embed.shadowRoot);
      if (!email) return;
      var now = Date.now();
      if (now - lastEmail < 4000) return; // guard double-clicks / re-renders
      lastEmail = now;
      rudderIdentify({ email: email });
      rudderTrack('email_captured', { form: 'shopify_forms' });
    } catch (e) { /* never break the page for analytics */ }
  }, true);
})();
